import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import axios from "axios";

import {
  Typography,
  IconButton,
  Button,
  Box,
  Paper,
  Collapse,
  TextField,
  MenuItem,
  Stack,
  Card,
  InputAdornment,
  Chip,
  Tooltip,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";

import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RuleIcon from "@mui/icons-material/Rule";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import AppHeader from "./AppHeader";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";



import { useNavigate } from "react-router-dom";

import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

ModuleRegistry.registerModules([AllCommunityModule]);

const toAttendanceDate = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Yangon", year: "numeric", month: "2-digit", day: "2-digit"
  }).formatToParts(new Date(value)).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const isIncompleteAttendance = (record) => record?.status === 'incomplete';
const isAbsentAttendance = (record) => record?.status === 'absent';

const getResolutionMode = (records = []) => {
  const normalized = records.filter(Boolean);
  if (normalized.length && normalized.every(isIncompleteAttendance)) return 'incomplete';
  if (normalized.length && normalized.every(isAbsentAttendance)) return 'absent';
  return 'mixed';
};

const INCOMPLETE_PAID_CATEGORIES = {
  NO_TA_ONLY: 'NO_TA_ONLY',
  PAID_NO_DEDUCTION: 'PAID_NO_DEDUCTION',
  WORKED_REMAINDER: 'WORKED_REMAINDER',
};

const resolveAttendancePayload = ({ preset, paidHours, unpaidHours, paidCategory, mode }) => {
  if (mode === 'incomplete') {
    if (preset === 'CONFIRM_WORKED_REMAINDER') {
      return {
        allocation_preset: 'CONFIRM_WORKED_REMAINDER',
        paid_minutes: 0,
        unpaid_minutes: 0,
        paid_category: INCOMPLETE_PAID_CATEGORIES.WORKED_REMAINDER,
      };
    }
    if (preset === 'NO_TA_ONLY') {
      return {
        allocation_preset: 'FULL_PAID',
        paid_minutes: 0,
        unpaid_minutes: 0,
        paid_category: INCOMPLETE_PAID_CATEGORIES.NO_TA_ONLY,
      };
    }
    if (preset === 'PAID_NO_DEDUCTION') {
      return {
        allocation_preset: 'FULL_PAID',
        paid_minutes: 0,
        unpaid_minutes: 0,
        paid_category: INCOMPLETE_PAID_CATEGORIES.PAID_NO_DEDUCTION,
      };
    }
    if (preset === 'HALF_DAY_UNPAID') {
      return {
        allocation_preset: 'HALF_UNPAID',
        paid_minutes: 0,
        unpaid_minutes: 0,
        paid_category: null,
      };
    }
    if (preset === 'FULL_DAY_UNPAID') {
      return {
        allocation_preset: 'FULL_UNPAID',
        paid_minutes: 0,
        unpaid_minutes: 0,
        paid_category: null,
      };
    }
  }

  const paid = Number(paidHours || 0);
  const unpaid = Number(unpaidHours || 0);
  const hasPaidAllocation = paid > 0 || ['FULL_PAID', 'HALF_PAID'].includes(preset);
  return {
    allocation_preset: preset,
    paid_minutes: Math.round(paid * 60),
    unpaid_minutes: Math.round(unpaid * 60),
    paid_category: hasPaidAllocation ? paidCategory : null,
  };
};

const getResolutionOptions = (mode) => {
  if (mode === 'incomplete') {
    return [
      { value: 'CONFIRM_WORKED_REMAINDER', label: 'Confirm Worked Remainder (Approved Half-Day Leave)' },
      { value: 'FORGOT_CLOCK_IN', label: 'Forgot Clock-In (Apply Fixed Fine)' },
      { value: 'FORGOT_CLOCK_OUT', label: 'Forgot Clock-Out (Apply Fixed Fine)' },
      { value: 'NO_TA_ONLY', label: 'No TA Only' },
      { value: 'HALF_DAY_UNPAID', label: 'Half Day Unpaid' },
      { value: 'FULL_DAY_UNPAID', label: 'Full Day Unpaid' },
      { value: 'PAID_NO_DEDUCTION', label: 'Paid, No Deduction' },
    ];
  }

  return [
    { value: 'FULL_PAID', label: mode === 'mixed' ? 'Full Day Paid / No Deduction' : 'Full Day Paid' },
    { value: 'FORGOT_CLOCK_BOTH', label: 'Forgot Clock Both (In & Out - Apply Fixed Fine)' },
    { value: 'HALF_PAID', label: 'Half Day Paid' },
    { value: 'FULL_UNPAID', label: 'Full Day Unpaid' },
    { value: 'HALF_UNPAID', label: 'Half Day Unpaid' },
    { value: 'CUSTOM', label: 'Custom / Split Hours' },
  ];
};

const formatAbsenceAllocation = ({ scheduled_minutes, paid_minutes, unpaid_minutes, paid_category, penalty_type, penalty_amount }) => {
  if (penalty_type === 'MISSED_IN') return `Forgot Clock-In (${Number(penalty_amount || 0).toLocaleString()} MMK Fine)`;
  if (penalty_type === 'MISSED_OUT') return `Forgot Clock-Out (${Number(penalty_amount || 0).toLocaleString()} MMK Fine)`;
  if (penalty_type === 'MISSED_BOTH') return `Forgot Clock Both (${Number(penalty_amount || 0).toLocaleString()} MMK Fine)`;
  const scheduled = Number(scheduled_minutes || 0);
  const paid = Number(paid_minutes || 0);
  const unpaid = Number(unpaid_minutes || 0);
  if (paid_category === INCOMPLETE_PAID_CATEGORIES.NO_TA_ONLY) return "No TA Only";
  if (paid_category === INCOMPLETE_PAID_CATEGORIES.PAID_NO_DEDUCTION) return "Paid, No Deduction";
  if (paid_category === INCOMPLETE_PAID_CATEGORIES.WORKED_REMAINDER) return "Worked Remainder (Approved Half-Day Leave)";
  if (scheduled > 0 && paid === scheduled && unpaid === 0) return "Full Day Paid";
  if (scheduled > 0 && unpaid === scheduled && paid === 0) return "Full Day Unpaid";
  if (scheduled > 0 && paid * 2 === scheduled && unpaid === 0) return "Half Day Paid";
  if (scheduled > 0 && unpaid * 2 === scheduled && paid === 0) return "Half Day Unpaid";
  return `Paid ${paid / 60}h / Unpaid ${unpaid / 60}h`;
};

// Fix leaflet icon issue in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});


const API_URL = process.env.REACT_APP_ATTENDANCE_API_URL || "http://localhost:3000/api/attendance";
const API_URL1 = process.env.REACT_APP_API_URL1;
const ATTENDANCE_LIST_GRID_KEY = "attendance-list";

const getTenantHeader = () => ({
  "X-Tenant": localStorage.getItem("tenant") || "",
});


const getBearerToken = () => {
  // Web admin may or may not store a token; keep optional.
  return (
    localStorage.getItem("userToken") ||
    localStorage.getItem("token") ||
    ""
  );
};

const buildHierarchicalList = (nodes = []) => {
  const byParent = {};
  nodes.forEach((node) => {
    const parentKey = node.parent_id || 'root';
    if (!byParent[parentKey]) byParent[parentKey] = [];
    byParent[parentKey].push(node);
  });

  const result = [];
  const walk = (parentId, depth = 0) => {
    (byParent[parentId || 'root'] || [])
      .sort((a, b) => String(a.node_name || '').localeCompare(String(b.node_name || '')))
      .forEach((node) => {
        result.push({ ...node, _depth: depth, level_type: node.level_type || node.type_name });
        walk(node.id, depth + 1);
      });
  };

  walk(null);
  return result;
};

const getColumnId = (colDef, index) => colDef.colId || colDef.field || `column_${index}`;

const buildColumnLabels = (colDefs = []) => {
  const labels = {};
  colDefs.forEach((colDef, index) => {
    const colId = getColumnId(colDef, index);
    labels[colId] = colDef.headerName || colDef.field || colId;
  });
  return labels;
};

const AttendanceList = ({ setIsLoggedIn }) => {
  console.log('🔄 Component rendered');

  const gridRef = useRef(null);
  const fetchDataRef = useRef(null);
  const columnLabelsRef = useRef({});
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [columnOptions, setColumnOptions] = useState([]);
  const [gridPreferenceLoading, setGridPreferenceLoading] = useState(false);
  const [gridPreferenceSaving, setGridPreferenceSaving] = useState(false);

  const [filtersOpen, setFiltersOpen] = useState(true);
  const getTodayStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const today = getTodayStr();

  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
    staff_code: "",
    full_name: "",
    org_node_id: "",
    branch_id: "",
    project_id: "",
    status: "",
  });

  // Separate state for applied filters (the ones sent to backend)
  const [appliedFilters, setAppliedFilters] = useState({
    date_from: today,
    date_to: today,
    staff_code: "",
    full_name: "",
    org_node_id: "",
    branch_id: "",
    project_id: "",
    status: "",
  });

  // Debounced auto-apply for dynamic searching
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters(filters);
      setPage(1);
    }, 400); // 400ms debounce
    return () => clearTimeout(timer);
  }, [filters]);


  const [rowData, setRowData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editClockIn, setEditClockIn] = useState("");
  const [editClockOut, setEditClockOut] = useState("");
  const [editOtHours, setEditOtHours] = useState("");
  const [editRemark, setEditRemark] = useState("");
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveRecord, setResolveRecord] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkResolveDialogOpen, setBulkResolveDialogOpen] = useState(false);
  const [bulkPaidHours, setBulkPaidHours] = useState(0);
  const [bulkUnpaidHours, setBulkUnpaidHours] = useState(0);
  const [bulkAllocationPreset, setBulkAllocationPreset] = useState("FULL_PAID");
  const [bulkPaidCategory, setBulkPaidCategory] = useState("");
  const [bulkResolutionReason, setBulkResolutionReason] = useState("");
  const [paidHours, setPaidHours] = useState(0);
  const [unpaidHours, setUnpaidHours] = useState(0);
  const [allocationPreset, setAllocationPreset] = useState("CUSTOM");
  const [paidCategory, setPaidCategory] = useState("");
  const [resolutionReason, setResolutionReason] = useState("");

  // Photo preview (signed URL)
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoTitle, setPhotoTitle] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  // Map state
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapLat, setMapLat] = useState(0);
  const [mapLng, setMapLng] = useState(0);
  const [mapTitle, setMapTitle] = useState("");

  const openMapDialog = (lat, lng, title) => {
    if (!lat || !lng) {
      alert("GPS coordinates not available for this location.");
      return;
    }
    setMapLat(parseFloat(lat));
    setMapLng(parseFloat(lng));
    setMapTitle(title || "Location Map");
    setMapDialogOpen(true);
  };

  const handleClosePhotoDialog = () => {
    setPhotoDialogOpen(false);

    if (photoUrl && photoUrl.startsWith("blob:")) {
      URL.revokeObjectURL(photoUrl);
    }

    setPhotoUrl("");
    setPhotoError("");
  };

  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / pageSize);

  const [page, setPage] = useState(1);
  const [headcount, setHeadcount] = useState(null);
  const [orgNodes, setOrgNodes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);

  const selectedResolvableRows = useMemo(() => (
    selectedRows.filter(row => row?.work_type === 'office' && ['absent', 'incomplete'].includes(row?.status))
  ), [selectedRows]);
  const bulkResolutionMode = useMemo(() => getResolutionMode(selectedResolvableRows), [selectedResolvableRows]);
  const singleResolutionMode = useMemo(() => getResolutionMode(resolveRecord ? [resolveRecord] : []), [resolveRecord]);
  const singleResolutionOptions = useMemo(() => getResolutionOptions(singleResolutionMode), [singleResolutionMode]);
  const bulkResolutionOptions = useMemo(() => getResolutionOptions(bulkResolutionMode), [bulkResolutionMode]);

  // Time Evaluation Process UI State
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);
  const [processStartDate, setProcessStartDate] = useState(today);
  const [processEndDate, setProcessEndDate] = useState(today);

  const handleProcessAttendance = async () => {
    if (!processStartDate || !processEndDate) {
      alert("Please select both Start Date and End Date.");
      return;
    }
    try {
      setProcessLoading(true);
      const res = await axios.post(`${API_URL}/admin/process-evaluation`, {
        startDate: processStartDate,
        endDate: processEndDate
      }, {
        withCredentials: true,
        headers: getTenantHeader(),
      });

      if (res.data.success) {
        setProcessDialogOpen(false);
        // Refresh grid and headcount
        fetchData(page, appliedFilters);
        fetchHeadcount(appliedFilters);
      }
    } catch (err) {
      console.error("Error processing attendance:", err);
      alert(err.response?.data?.message || "Failed to process attendance.");
    } finally {
      setProcessLoading(false);
    }
  };

  useEffect(() => {
    const fetchFilterMetadata = async () => {
      try {
        const token = getBearerToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const config = {
          withCredentials: true,
          headers: {
            ...getTenantHeader(),
            ...authHeaders,
          },
        };
        const [nodesRes, branchesRes] = await Promise.all([
          axios.get(`${API_URL1}/som/nodes`, config),
          axios.get(`${API_URL1}/som/branches`, config),
        ]);
        if (nodesRes.data?.success) setOrgNodes(buildHierarchicalList(nodesRes.data.data || []));
        if (branchesRes.data?.success) setBranches(branchesRes.data.data || []);
      } catch (err) {
        console.error("Failed to load attendance filter metadata:", err);
      }
    };

    fetchFilterMetadata();
  }, []);

  useEffect(() => {
    const fetchProjectsList = async () => {
      try {
        const token = getBearerToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const config = {
          withCredentials: true,
          headers: {
            ...getTenantHeader(),
            ...authHeaders,
          },
        };
        const params = new URLSearchParams();
        if (filters.date_from && filters.date_to) {
          params.append("startDate", filters.date_from);
          params.append("endDate", filters.date_to);
        } else {
          params.append("status", "active");
        }
        const res = await axios.get(`${API_URL1}/sm/projects?${params.toString()}`, config);
        if (res.data?.success) {
          setProjects(res.data.data || []);
        }
      } catch (err) {
        console.error("Failed to load projects list for filters:", err);
      }
    };

    fetchProjectsList();
  }, [filters.date_from, filters.date_to]);

  const navigate = useNavigate();

  console.log('📊 Current State:', {
    filters,
    appliedFilters,
    page,
    rowDataLength: rowData.length
  });


  // Convert DB time → Local Myanmar Time for datetime input
  const convertToLocalInputFormat = (val) => {
    if (!val) return "";
    const d = new Date(val);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    const local = new Date(d.getTime() - tzOffsetMs);
    return local.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };

  const addDaysToDateKey = (dateKey, days) => {
    if (!dateKey || !days) return dateKey;
    const [year, month, day] = dateKey.split("-").map(Number);
    const date = new Date(year, month - 1, day + days);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const buildPlannedDateTimeInput = (attendanceDate, timeValue, addDays = 0) => {
    const dateKey = addDaysToDateKey(toAttendanceDate(attendanceDate), addDays);
    const timeKey = String(timeValue || "").slice(0, 5);
    return dateKey && timeKey ? `${dateKey}T${timeKey}` : "";
  };


  const handleExportExcel = async () => {
    try {
      // Export should be based on applied filters (server-side export),
      // not on the current page's rowData.
      if (!appliedFilters?.date_from || !appliedFilters?.date_to) {
        alert("Please select From Date and To Date first.");
        return;
      }

      const tenantHeaders = getTenantHeader();
      const token = getBearerToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // Build query params from applied filters (same as list)
      const params = {
        ...appliedFilters,
        // Server export should ignore pagination, but we won't send page/pageSize anyway.
      };

      // IMPORTANT: This endpoint must return an XLSX file (Content-Type:
      // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet)
      const exportUrl = `${API_URL}/admin/export-v2`;

      const res = await axios.get(exportUrl, {
        params,
        withCredentials: true,
        headers: {
          ...tenantHeaders,
          ...authHeaders,
        },
        responseType: "blob",
      });

      // Try to read filename from Content-Disposition
      const disposition = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      let fileName = "";

      if (disposition && typeof disposition === "string") {
        // e.g. attachment; filename="attendance_TNL_2026-01-01-2026-01-31.xlsx"
        const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1].replace(/"/g, "").trim());
        }
      }

      if (!fileName) {
        const from = appliedFilters?.date_from || "";
        const to = appliedFilters?.date_to || "";
        const tenant = (localStorage.getItem("tenant") || "").trim();
        const safeTenant = tenant ? tenant.replace(/[^a-zA-Z0-9_-]/g, "_") : "TENANT";
        fileName = `attendance_${safeTenant}_${from || "from"}-${to || "to"}.xlsx`;
      }

      // Download blob
      const blob = new Blob([res.data], {
        type:
          res.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("❌ Server export failed:", e);
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Export failed. Please try again.";
      alert(msg);
    }
  };

  const openPhotoPreview = useCallback(async (documentId, title) => {
    if (!documentId) return;

    setPhotoTitle(title || "Photo");
    // Clear previous URL and error
    setPhotoUrl("");
    setPhotoError("");
    setPhotoLoading(true);
    setPhotoDialogOpen(true);

    const tenantHeaders = getTenantHeader();

    try {
      // 1) Attempt to get Signed URL from Attendance Backend
      const previewRes = await axios.get(
        `${API_URL1}/admin/documents/${encodeURIComponent(documentId)}/preview`,
        {
          withCredentials: true,
          headers: { ...tenantHeaders },
        }
      );

      if (previewRes?.data?.url) {
        setPhotoUrl(previewRes.data.url);
        setPhotoLoading(false);
        return;
      }

      throw new Error("No URL returned from backend");
    } catch (e1) {
      console.warn("⚠️ Photo preview via backend failed; fetching binary from DMS...", e1);

      try {
        // 2) Fallback: Fetch the binary image data directly from DMS
        const token = getBearerToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        const dmsRes = await axios.get(
          `${API_URL1}/admin/documents/${encodeURIComponent(documentId)}/download`,
          {
            withCredentials: true,
            headers: {
              ...authHeaders,
              ...tenantHeaders,
            },
            // CRITICAL CHANGE: Tell Axios to expect binary data
            responseType: 'blob',
          }
        );

        // Check if the content type is an image
        const contentType = dmsRes.headers['content-type'];

        if (contentType && contentType.startsWith('image')) {
          // Create a local object URL from the Blob
          const blob = new Blob([dmsRes.data], { type: contentType });
          const objectUrl = URL.createObjectURL(blob);

          setPhotoUrl(objectUrl);
          setPhotoLoading(false);
        } else {
          // If we got a blob but it's not an image (e.g. JSON error inside a blob)
          // We try to read it to see the error message
          const text = await dmsRes.data.text();
          let errorMessage = "Invalid content type received";
          try {
            const json = JSON.parse(text);
            if (json.message) errorMessage = json.message;
          } catch (e) { /* ignore */ }

          throw new Error(errorMessage);
        }

      } catch (e2) {
        console.error("❌ Photo load failed", e2);
        setPhotoError("Failed to load photo.");
        setPhotoLoading(false);
      }
    }
  }, []);

  const deleteAttendanceRecord = useCallback(async (recordId, label = "shift") => {
    if (!recordId) {
      alert("No attendance record found to delete.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete this ${label}?`)) return;

    try {
      await axios.post(`${API_URL}/admin/update-core`, {
        record_id: recordId,
        updates: { is_delete: true }
      }, {
        withCredentials: true,
        headers: getTenantHeader()
      });
      alert("Attendance record deleted successfully");
      fetchDataRef.current?.(page, appliedFilters);
    } catch (err) {
      console.error("Error deleting attendance record:", err);
      alert("Error deleting attendance record");
    }
  }, [appliedFilters, page]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        colId: "expand",
        field: "expand",
        width: 35,
        pinned: "left",
        lockVisible: true,
        cellRenderer: params => {
          const isOffice = params.data?.work_type === "office";
          const hasMultiple = Array.isArray(params.data?.shifts) && params.data.shifts.length > 1;
          if (!isOffice || !hasMultiple) return "";
          const isOpen = expandedRows[params.data.id];
          return (
            <IconButton
              size="small"
              onClick={() => {
                setExpandedRows(prev => ({
                  ...prev,
                  [params.data.id]: !prev[params.data.id]
                }));
              }}
              sx={{ color: '#1b5e20' }}
            >
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          );
        }
      },
      {
        headerName: "ID",
        field: "id",
        width: 50,
        cellStyle: { fontWeight: 'bold', color: '#1a237e' }
      },
      {
        headerName: "Employee No.",
        field: "employee_number",
        width: 120,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        cellStyle: { fontWeight: 500 }
      },
      {
        headerName: "Name",
        field: "full_name",
        width: 140,
        pinned: "left",
        cellStyle: { fontWeight: 500 }
      },
      { headerName: "Department", colId: "department_primary", field: "department", width: 150 },
      { headerName: "Branch", field: "branch_name", width: 140 },
      {
        headerName: "Date",
        field: "attendance_date",
        width: 105,
        pinned: "left",
        valueFormatter: params => {
          if (!params.value) return "";
          return new Date(params.value).toLocaleDateString("en-GB");
        }
      },
      {
        headerName: "Day Type",
        field: "day_type",
        width: 80,
        cellRenderer: (params) => {
          if (!params.value) return "-";
          const isWork = params.value === "WORK";
          return (
            <Typography variant="body2" sx={{ color: isWork ? 'text.secondary' : 'error.main', fontWeight: isWork ? 'normal' : 'bold' }}>
              {params.value}
            </Typography>
          );
        }
      },
      {
        headerName: "Shift",
        field: "shift_code",
        width: 80,
        cellRenderer: (params) => {
          if (!params.value) return params.data?.day_type === 'UNPLANNED' ? <Chip label="No schedule" size="small" color="warning" variant="outlined" /> : "-";
          return (
            <Tooltip title={`${params.data?.shift_start_time || '?'} - ${params.data?.shift_end_time || '?'}`}>
              <Chip label={params.value} size="small" variant="outlined" color="primary" />
            </Tooltip>
          );
        }
      },
      {
        headerName: "Start Time",
        field: "shift_start_time",
        width: 80,
        valueFormatter: params => params.value || "-",
      },
      {
        headerName: "End Time",
        field: "shift_end_time",
        width: 80,
        valueFormatter: params => params.value || "-",
      },
      {
        headerName: "Clock In",
        field: "clock_in_time",
        width: 100,
        valueFormatter: params => {
          if (!params.value) return "";
          return new Date(params.value).toLocaleString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          });
        }
      },
      {
        headerName: "Clock Out",
        field: "clock_out_time",
        width: 100,
        valueFormatter: params => {
          if (!params.value) return "";
          return new Date(params.value).toLocaleString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          });
        }
      },
      {
        headerName: "Late (Mins)",
        field: "late_minutes",
        width: 80,
        cellRenderer: (params) => {
          if (!params.value) return "-";
          return (
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
              {params.value} mins
            </Typography>
          );
        }
      },
      {
        headerName: "Early Leave (Mins)",
        field: "early_leave_minutes",
        width: 95,
        cellRenderer: (params) => {
          if (!params.value) return "-";
          return (
            <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
              {params.value} mins
            </Typography>
          );
        }
      },
      { headerName: "Shift Hrs", field: "total_shift_hours", width: 80 },
      { headerName: "Sys OT Hrs", field: "system_ot_hours", width: 80, cellStyle: { color: 'blue' } },
      { headerName: "OT Hrs", field: "ot_hours", width: 75 },
      {
        headerName: "In Location",
        field: "clock_in_location_name",
        width: 120,
        cellRenderer: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
              {params.value || "-"}
            </Typography>
            {params.data?.clock_in_latitude && (
              <Tooltip title="View on Map">
                <IconButton size="small" onClick={() => openMapDialog(params.data.clock_in_latitude, params.data.clock_in_longitude, "Clock-In Location")}>
                  <VisibilityIcon fontSize="inherit" color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      },
      {
        headerName: "Out Location",
        field: "clock_out_location_name",
        width: 120,
        cellRenderer: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
              {params.value || "-"}
            </Typography>
            {params.data?.clock_out_latitude && (
              <Tooltip title="View on Map">
                <IconButton size="small" onClick={() => openMapDialog(params.data.clock_out_latitude, params.data.clock_out_longitude, "Clock-Out Location")}>
                  <VisibilityIcon fontSize="inherit" color="secondary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      },
      {
        headerName: "Photos",
        colId: "photos",
        width: 130,
        cellRenderer: (params) => {
          const inDoc = params.data?.clock_in_document_id;
          const outDoc = params.data?.clock_out_document_id;
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {inDoc && (
                <Tooltip title="In Photo">
                  <IconButton size="small" onClick={() => openPhotoPreview(inDoc, "In Photo")}>
                    <VisibilityIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
              {outDoc && (
                <Tooltip title="Out Photo">
                  <IconButton size="small" onClick={() => openPhotoPreview(outDoc, "Out Photo")}>
                    <VisibilityIcon fontSize="small" color="secondary" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        }
      },
      { headerName: "Department", colId: "department_secondary", field: "department", width: 150 },
      {
        headerName: "Type",
        field: "work_type",
        width: 100,
        cellRenderer: (params) => (
          <Chip
            label={params.value === 'office' && (Number(params.data?.allow_split_shift || 0) === 1 || (Array.isArray(params.data?.shifts) && params.data.shifts.length > 1)) ? 'Office Split' : params.value}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: '24px',
              borderColor: params.value === 'office' ? '#1976d2' : '#9c27b0',
              color: params.value === 'office' ? '#1976d2' : '#9c27b0'
            }}
          />
        )
      },
      {
        headerName: "Leave Details",
        field: "leave_type",
        width: 300,
        autoHeight: true,
        cellRenderer: (params) => {
          const entries = Array.isArray(params.data?.leave_entries)
            ? params.data.leave_entries
            : [];
          if (entries.length === 0 && !params.value) return "-";

          const displayEntries = entries.length > 0 ? entries : [{
            type_name: params.value,
            status: params.data.leave_status,
            reason: params.data.leave_reason,
            day_portion: params.data.leave_day_portion,
            days: params.data.leave_day_portion === "full" ? 1 : 0.5,
          }];
          const formatPortion = (portion) => {
            if (portion === "morning") return "Morning";
            if (portion === "afternoon") return "Afternoon";
            return "Full Day";
          };
          const totalDays = params.data?.leave_total_days
            ?? Math.min(1, displayEntries.reduce((sum, entry) => sum + Number(entry.days || 0), 0));

          return (
            <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
              {displayEntries.map((entry, index) => (
                <Tooltip
                  key={entry.id || `${entry.type_name}-${entry.day_portion}-${index}`}
                  title={entry.reason || "No reason"}
                >
                  <Chip
                    label={`${entry.type_name} · ${formatPortion(entry.day_portion)} · ${Number(entry.days || 0).toFixed(1)} day · ${entry.status || ""}`}
                    size="small"
                    variant="outlined"
                    color={String(entry.status).toLowerCase() === 'approved' ? 'success' : 'warning'}
                    sx={{ fontSize: '0.68rem', height: '24px', maxWidth: '100%' }}
                  />
                </Tooltip>
              ))}
              <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>
                {displayEntries.length} {displayEntries.length === 1 ? 'request' : 'requests'} · {Number(totalDays).toFixed(1)} day total
              </Box>
            </Box>
          );
        }
      },
      {
        headerName: "Absence Resolution",
        field: "resolution_status",
        width: 190,
        cellRenderer: params => params.value ? (
          <Chip size="small" color={params.value === 'APPROVED' ? 'success' : 'warning'} label={`${params.value} · ${formatAbsenceAllocation(params.data)}`} />
        ) : '-'
      },
      {
        headerName: "Status",
        field: "status",
        width: 85,
        cellRenderer: (params) => {
          const status = params.value?.toLowerCase() || '';
          let color = 'default';
          if (status === 'present' || status === 'on time') color = 'success';
          else if (status === 'late' || status === 'absent') color = 'error';
          else if (status === 'incomplete') color = 'warning';

          return (
            <Chip
              label={params.value || '-'}
              size="small"
              color={color}
              sx={{ fontWeight: 600, fontSize: '0.7rem', height: '24px' }}
            />
          );
        }
      },
      { headerName: "Notes", field: "notes", width: 200 },
      { headerName: "Remark", field: "remark", width: 200, editable: false },
      {
        headerName: "Action",
        colId: "action",
        width: 130,
        pinned: "right",
        lockVisible: true,
        cellRenderer: (params) => {
          return (
            <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit attendance"><IconButton size="small" onClick={() => {
              const r = params.data;
              if (r.work_type === "office" && Array.isArray(r.shifts) && r.shifts.length > 1) {
                alert("Multiple shifts - expand and edit a specific shift.");
                return;
              }
              setEditRecord(r);
              setEditClockIn(convertToLocalInputFormat(r.clock_in_time));
              setEditClockOut(convertToLocalInputFormat(r.clock_out_time));
              setEditOtHours(r.ot_hours !== undefined && r.ot_hours !== null ? String(r.ot_hours) : "");
              setEditRemark(r.remark || "");
              setEditDialogOpen(true);
            }}>
              <EditIcon fontSize="small" />
            </IconButton></Tooltip>
          {params.data.work_type !== 'office' && params.data.id && (
              <Tooltip title="Delete attendance"><IconButton color="error" size="small" onClick={() => deleteAttendanceRecord(params.data.id, "attendance record")}>
                <DeleteIcon fontSize="small" />
              </IconButton></Tooltip>
            )}
          {params.data.work_type === 'office' && ['absent', 'incomplete'].includes(params.data.status) && (
              <Tooltip title="Resolve attendance for payroll"><IconButton color="warning" size="small" onClick={() => {
                setResolveRecord(params.data);
                setPaidHours(Number(params.data.paid_minutes || 0) / 60);
                setUnpaidHours(Number(params.data.unpaid_minutes || 0) / 60);
                setAllocationPreset(
                  params.data.status === 'incomplete'
                    ? (params.data.day_type === 'LEAVE' ? "CONFIRM_WORKED_REMAINDER" : "NO_TA_ONLY")
                    : "CUSTOM"
                );
                setPaidCategory("");
                setResolutionReason("");
                setResolveDialogOpen(true);
              }}><RuleIcon fontSize="small" /></IconButton></Tooltip>
            )}
            </Stack>
          );
        }
      }
    ],
    [deleteAttendanceRecord, expandedRows, openPhotoPreview]
  );

  useEffect(() => {
    columnLabelsRef.current = buildColumnLabels(columnDefs);
  }, [columnDefs]);

  const refreshColumnOptions = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api?.getColumnState) return;
    const labels = columnLabelsRef.current || {};
    const locked = new Set(["expand", "action"]);
    const options = api.getColumnState()
      .filter((col) => !locked.has(col.colId))
      .map((col) => ({
        colId: col.colId,
        label: labels[col.colId] || col.colId,
        visible: !col.hide,
      }));
    setColumnOptions(options);
  }, []);

  const applyGridPreference = useCallback((gridState) => {
    const api = gridRef.current?.api;
    if (!api || !gridState) return;
    if (Array.isArray(gridState.columnState) && api.applyColumnState) {
      api.applyColumnState({ state: gridState.columnState, applyOrder: true });
    }
    if (gridState.filterModel && api.setFilterModel) {
      api.setFilterModel(gridState.filterModel);
    }
    refreshColumnOptions();
  }, [refreshColumnOptions]);

  const loadGridPreference = useCallback(async () => {
    try {
      setGridPreferenceLoading(true);
      const token = getBearerToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL1}/users/grid-preferences/${ATTENDANCE_LIST_GRID_KEY}`, {
        params: { view_name: "default" },
        withCredentials: true,
        headers: { ...getTenantHeader(), ...authHeaders },
      });
      if (res.data?.success && res.data?.data?.grid_state) {
        applyGridPreference(res.data.data.grid_state);
      } else {
        refreshColumnOptions();
      }
    } catch (err) {
      console.error("Failed to load attendance grid preference:", err);
      refreshColumnOptions();
    } finally {
      setGridPreferenceLoading(false);
    }
  }, [applyGridPreference, refreshColumnOptions]);

  const handleGridReady = useCallback(() => {
    refreshColumnOptions();
    loadGridPreference();
  }, [loadGridPreference, refreshColumnOptions]);

  const handleToggleColumn = useCallback((colId, visible) => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (api.setColumnsVisible) {
      api.setColumnsVisible([colId], visible);
    } else if (api.setColumnVisible) {
      api.setColumnVisible(colId, visible);
    }
    refreshColumnOptions();
  }, [refreshColumnOptions]);

  const handleSaveGridPreference = useCallback(async () => {
    const api = gridRef.current?.api;
    if (!api?.getColumnState) return;
    try {
      setGridPreferenceSaving(true);
      const token = getBearerToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL1}/users/grid-preferences/${ATTENDANCE_LIST_GRID_KEY}`, {
        view_name: "default",
        grid_state: {
          version: 1,
          columnState: api.getColumnState(),
          filterModel: api.getFilterModel ? api.getFilterModel() : null,
          savedAt: new Date().toISOString(),
        },
      }, {
        withCredentials: true,
        headers: { ...getTenantHeader(), ...authHeaders },
      });
      alert("Attendance List column view saved.");
    } catch (err) {
      console.error("Failed to save attendance grid preference:", err);
      alert(err.response?.data?.message || "Could not save column view.");
    } finally {
      setGridPreferenceSaving(false);
    }
  }, []);

  const handleResetGridPreference = useCallback(async () => {
    const api = gridRef.current?.api;
    if (!api) return;
    try {
      setGridPreferenceSaving(true);
      if (api.resetColumnState) api.resetColumnState();
      if (api.setFilterModel) api.setFilterModel(null);
      const token = getBearerToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_URL1}/users/grid-preferences/${ATTENDANCE_LIST_GRID_KEY}`, {
        params: { view_name: "default" },
        withCredentials: true,
        headers: { ...getTenantHeader(), ...authHeaders },
      });
      refreshColumnOptions();
    } catch (err) {
      console.error("Failed to reset attendance grid preference:", err);
      alert(err.response?.data?.message || "Could not reset column view.");
    } finally {
      setGridPreferenceSaving(false);
    }
  }, [refreshColumnOptions]);

  const DetailRenderer = ({ data }) => {
    if (data?.work_type !== "office" || !data?.shifts || data.shifts.length <= 1) return null;
    return (
      <Box sx={{ p: 2, bgcolor: '#f1f8e9', borderLeft: '4px solid #1b5e20', ml: 7, my: 1, borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1b5e20', mb: 1 }}>
          Shift Details
        </Typography>
        <Stack spacing={1}>
          {data.shifts.map((s, idx) => (
            <Paper key={idx} sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #c8e6c9' }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Shift ID</Typography>
                  <Typography variant="body2">{s.id || 'Planned'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Shift</Typography>
                  <Typography variant="body2">{s.shift_code || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Planned</Typography>
                  <Typography variant="body2">{s.shift_start_time || '-'} - {s.shift_end_time || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Clock In</Typography>
                  <Typography variant="body2">
                    {s.clock_in_time ? new Date(s.clock_in_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Clock Out</Typography>
                  <Typography variant="body2">
                    {s.clock_out_time ? new Date(s.clock_out_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">In Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">{s.clock_in_location_name || '-'}</Typography>
                    {s.clock_in_latitude && (
                      <IconButton size="small" onClick={() => openMapDialog(s.clock_in_latitude, s.clock_in_longitude, "Clock-In Location")}>
                        <VisibilityIcon sx={{ fontSize: 14 }} color="primary" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Out Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">{s.clock_out_location_name || '-'}</Typography>
                    {s.clock_out_latitude && (
                      <IconButton size="small" onClick={() => openMapDialog(s.clock_out_latitude, s.clock_out_longitude, "Clock-Out Location")}>
                        <VisibilityIcon sx={{ fontSize: 14 }} color="secondary" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {s.clock_in_document_id && (
                  <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => openPhotoPreview(s.clock_in_document_id, "Clock-In Photo")}>
                    In Photo
                  </Button>
                )}
                {s.clock_out_document_id && (
                  <Button size="small" variant="outlined" color="secondary" startIcon={<VisibilityIcon />} onClick={() => openPhotoPreview(s.clock_out_document_id, "Clock-Out Photo")}>
                    Out Photo
                  </Button>
                )}
                {s.is_planned_only ? (
                  <>
                    <Chip label="Planned" size="small" variant="outlined" />
                    <Button size="small" variant="contained" color="primary" onClick={() => {
                      setEditRecord({
                        ...s,
                        id: null,
                        isSubShift: true,
                        isPlannedShift: true,
                        work_type: 'sub_shift',
                        staff_id: data.staff_id,
                        attendance_date: data.attendance_date,
                        shift_code: s.shift_code,
                        shift_start_time: s.shift_start_time,
                        shift_end_time: s.shift_end_time
                      });
                      const isOvernightShift = s.shift_start_time && s.shift_end_time && String(s.shift_end_time) <= String(s.shift_start_time);
                      setEditClockIn(buildPlannedDateTimeInput(data.attendance_date, s.shift_start_time));
                      setEditClockOut(buildPlannedDateTimeInput(data.attendance_date, s.shift_end_time, isOvernightShift ? 1 : 0));
                      setEditOtHours("");
                      setEditRemark("");
                      setEditDialogOpen(true);
                    }}>
                      Edit
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="small" variant="contained" color="primary" onClick={() => {
                      setEditRecord({ ...s, isSubShift: true, work_type: 'sub_shift', staff_id: data.staff_id, attendance_date: data.attendance_date });
                      setEditClockIn(convertToLocalInputFormat(s.clock_in_time));
                      setEditClockOut(convertToLocalInputFormat(s.clock_out_time));
                      setEditOtHours(s.ot_hours !== undefined && s.ot_hours !== null ? String(s.ot_hours) : "");
                      setEditRemark(s.remark || "");
                      setEditDialogOpen(true);
                    }}>
                      Edit
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={async () => {
                      if (window.confirm("Are you sure you want to delete this shift?")) {
                        try {
                          await axios.post(`${API_URL}/admin/update-core`, {
                            record_id: s.id,
                            updates: { is_delete: true }
                          }, { withCredentials: true, headers: { "X-Tenant": localStorage.getItem("tenant") || "" } });
                          alert("Shift deleted successfully");
                          fetchData(page, filters);
                        } catch (err) {
                          console.error("Error deleting shift:", err);
                          alert("Error deleting shift");
                        }
                      }
                    }}>
                      Delete
                    </Button>
                  </>
                )}
              </Box>
            </Paper>
          ))}
        </Stack>
      </Box>
    );
  };

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    };
  }, []);

  // Use useCallback to memoize the fetch function
  const fetchData = useCallback(async (currentPage, currentFilters) => {
    console.log('🚀 fetchData CALLED');
    console.log('🔍 Fetching with filters:', currentFilters);
    console.log('📄 Page:', currentPage);

    try {
      const url = `${API_URL}/admin/list-v2`;
      const params = {
        page: currentPage,
        pageSize,
        ...currentFilters,
      };

      console.log('🌐 API URL:', url);
      console.log('📦 Request params:', params);

      const res = await axios.get(url, {
        params,
        withCredentials: true,
        headers: {
          "X-Tenant": localStorage.getItem("tenant") || "",
        },
      });

      console.log('✅ Response received:', res.data);

      if (res.data?.success) {
        const mapped = res.data.rows.map((r) => {
          const mappedItem = {
            id: r.record_id,
            staff_id: r.staff_id,
            staff_code: r.staff_code,
            employee_number: r.employee_number || r.staff_code,
            full_name: r.full_name,
            department: r.department,
            org_node_id: r.org_node_id,
            branch_id: r.branch_id,
            branch_name: r.branch_name,
            work_type: r.work_type,
            allow_split_shift: r.allow_split_shift || 0,
            attendance_date: r.attendance_date,
            clock_in_time: r.clock_in_time,
            clock_out_time: r.clock_out_time,
            clock_in_location_name: r.clock_in_location_name,
            clock_out_location_name: r.clock_out_location_name,
            clock_in_beacon_name: r.clock_in_beacon_name,
            clock_out_beacon_name: r.clock_out_beacon_name,
            clock_in_method: r.clock_in_method,
            total_shift_hours: r.total_shift_hours,
            system_ot_hours: r.system_ot_hours,
            ot_hours: r.ot_hours,
            day_type: r.day_type,
            shift_code: r.shift_code,
            shift_start_time: r.shift_start_time,
            shift_end_time: r.shift_end_time,
            status: r.status,
            notes: r.notes,
            remark: r.remark,
            clock_in_document_id: r.clock_in_document_id || null,
            clock_out_document_id: r.clock_out_document_id || null,
            shifts: r.shifts,
            late_minutes: r.late_minutes,
            early_leave_minutes: r.early_leave_minutes,
            is_early_leave: r.is_early_leave,
            leave_type: r.leave_type,
            leave_status: r.leave_status,
            leave_reason: r.leave_reason,
            leave_day_portion: r.leave_day_portion,
            leave_entries: r.leave_entries || [],
            leave_request_count: r.leave_request_count || 0,
            leave_total_days: Number(r.leave_total_days || 0),
            resolution_id: r.resolution_id,
            resolution_status: r.resolution_status,
            scheduled_minutes: r.scheduled_minutes,
            paid_minutes: r.paid_minutes,
            unpaid_minutes: r.unpaid_minutes,
            paid_category: r.paid_category,
            penalty_type: r.penalty_type,
            penalty_amount: r.penalty_amount,
            clock_in_latitude: r.clock_in_latitude,
            clock_in_longitude: r.clock_in_longitude,
            clock_out_latitude: r.clock_out_latitude,
            clock_out_longitude: r.clock_out_longitude,
          };
          if (r.work_type === "office" && Array.isArray(r.shifts) && r.shifts.length === 1) {
            mappedItem.clock_in_beacon_name = r.shifts[0].clock_in_beacon_name;
            mappedItem.clock_out_beacon_name = r.shifts[0].clock_out_beacon_name;
            mappedItem.clock_in_location_name = r.shifts[0].clock_in_location_name;
            mappedItem.clock_out_location_name = r.shifts[0].clock_out_location_name;
            mappedItem.clock_in_method = r.shifts[0].method || r.shifts[0].clock_in_method || mappedItem.clock_in_method;
            mappedItem.clock_in_document_id = r.shifts[0].clock_in_document_id || mappedItem.clock_in_document_id;
            mappedItem.clock_out_document_id = r.shifts[0].clock_out_document_id || mappedItem.clock_out_document_id;
            mappedItem.shift_code = r.shifts[0].shift_code || mappedItem.shift_code;
            mappedItem.shift_start_time = r.shifts[0].shift_start_time || mappedItem.shift_start_time;
            mappedItem.shift_end_time = r.shifts[0].shift_end_time || mappedItem.shift_end_time;
            mappedItem.clock_in_latitude = r.shifts[0].clock_in_latitude || mappedItem.clock_in_latitude;
            mappedItem.clock_in_longitude = r.shifts[0].clock_in_longitude || mappedItem.clock_in_longitude;
            mappedItem.clock_out_latitude = r.shifts[0].clock_out_latitude || mappedItem.clock_out_latitude;
            mappedItem.clock_out_longitude = r.shifts[0].clock_out_longitude || mappedItem.clock_out_longitude;
          }
          return mappedItem;
        });

        console.log('📝 Mapped data:', mapped);
        setRowData(mapped);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error("❌ Error fetching attendance:", err);
      console.error("Error details:", err.response?.data);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  const fetchHeadcount = useCallback(async (currentFilters) => {
    try {
      const targetDate = currentFilters.date_to || currentFilters.date_from || new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API_URL}/admin/daily-headcount`, {
        params: { date: targetDate },
        withCredentials: true,
        headers: getTenantHeader(),
      });
      if (res.data.success) {
        setHeadcount(res.data.summary);
      }
    } catch (err) {
      console.error("Error fetching headcount:", err);
    }
  }, []);

  // Fetch data when page or appliedFilters change
  useEffect(() => {
    console.log('⚡ useEffect TRIGGERED');
    console.log('⚡ Dependencies:', { page, appliedFilters });

    // Do NOT load automatically — require date range first
    if (!appliedFilters.date_from || !appliedFilters.date_to) {
      console.log('⏳ Skipping fetch — waiting for date range');
      setRowData([]); // ensure empty grid
      return;
    }

    fetchData(page, appliedFilters);
    fetchHeadcount(appliedFilters);
  }, [page, appliedFilters, fetchData, fetchHeadcount]);

  // Handler for Apply button - updates appliedFilters which triggers useEffect
  const handleApplyFilters = () => {
    console.log('🎯 handleApplyFilters CLICKED');
    console.log('🎯 Current filters:', filters);
    console.log('🎯 Setting appliedFilters to:', filters);

    setAppliedFilters({ ...filters }); // Create new object reference
    setPage(1);

    console.log('🎯 State update dispatched');
  };

  const handleResetFilters = () => {
    console.log('🔄 handleResetFilters CLICKED');

    const resetFilters = {
      date_from: "",
      date_to: "",
      staff_code: "",
      full_name: "",
      org_node_id: "",
      branch_id: "",
      status: "",
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setPage(1);
  };

  // Handler for manual refresh button
  const handleRefresh = () => {
    console.log('🔃 handleRefresh CLICKED — reload today');
    const todayFilters = {
      date_from: getTodayStr(),
      date_to: getTodayStr(),
      staff_code: "",
      full_name: "",
      org_node_id: "",
      branch_id: "",
      status: "",
    };
    setFilters(todayFilters);
    setAppliedFilters(todayFilters);
    setPage(1);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setIsLoggedIn(false);
    navigate("/login");
  };

  const HeadcountCards = () => {
    if (!headcount) return null;
    const targetDate = appliedFilters.date_to || appliedFilters.date_from || new Date().toISOString().split('T')[0];

    const cards = [
      { label: "Total Staff", value: headcount.total_staff, color: "#1a237e", filter: "" },
      { label: "Present", value: headcount.present, color: "#2e7d32", filter: "present" },
      { label: "Workday Absent", value: headcount.absent, color: "#c62828", filter: "absent" },
      { label: "On Leave", value: headcount.on_leave, color: "#0288d1", filter: "leave" },
      { label: "Pending Leave", value: headcount.pending_leave, color: "#7b1fa2", filter: "" },
      { label: "Rest Day", value: headcount.rest_day, color: "#607d8b", filter: "rest" },
      { label: "Late", value: headcount.late, color: "#ed6c02", filter: "late" },
    ];

    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#475569" }}>
            Daily Snapshot:
          </Typography>
          <Chip
            label={new Date(targetDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            color="primary"
            size="small"
            sx={{ fontWeight: 600, borderRadius: 1.5 }}
          />
        </Stack>
        <Grid container spacing={1}>
          {cards.map((card, idx) => (
            <Grid item xs={6} sm={4} md={2} lg={1.7} key={idx}>
              <Card
                onClick={() => {
                  if (card.filter !== "") {
                    const newFilters = { ...filters, status: card.filter };
                    setFilters(newFilters);
                    setAppliedFilters(newFilters);
                    setPage(1);
                  }
                }}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  textAlign: 'center',
                  borderTop: `3px solid ${card.color}`,
                  cursor: card.filter !== "" ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  '&:hover': card.filter !== "" ? {
                    backgroundColor: '#f8fafc',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  } : {}
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0, lineHeight: 1.1 }}>
                  {card.label}
                </Typography>
                  <Typography variant="h5" sx={{ color: card.color, fontWeight: 800, mt: 0.25, lineHeight: 1.15 }}>
                  {card.value || 0}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Convert datetime-local → MySQL local time string
  const toMySQL = (value) => {
    if (!value) return null;
    return value.replace("T", " ") + ":00";
  };

  const saveAttendanceChanges = async () => {
    try {
      if (!editRecord) return;

      const updates = {};

      if (editClockIn) {
        updates.clock_in_time = toMySQL(editClockIn);
      }
      if (editClockOut) {
        updates.clock_out_time = toMySQL(editClockOut);
      }
      if (editOtHours !== String(editRecord.ot_hours || "")) {
        updates.ot_hours = editOtHours === "" ? 0 : parseFloat(editOtHours);
      }

      const remarkChanged = editRemark !== (editRecord.remark || "");

      if (Object.keys(updates).length === 0 && !remarkChanged) {
        alert("No changes to save.");
        return;
      }

      // Derive correct attendanceId before posting
      let attendanceId = editRecord.id;
      let isNew = false;
      let savedRecordId = attendanceId;

      if (editRecord.work_type === "office") {
        if (Array.isArray(editRecord.shifts)) {
          if (editRecord.shifts.length === 1) {
            attendanceId = editRecord.shifts[0].id;
          } else if (editRecord.shifts.length === 0) {
            attendanceId = null;
            isNew = true;
          } else {
            alert("Multiple shifts detected. Expand and select a shift to edit.");
            return;
          }
        }
      }
      if (editRecord.isPlannedShift && !attendanceId) {
        isNew = true;
      }

      if (Object.keys(updates).length > 0) {
        const updateRes = await axios.post(
          `${API_URL}/admin/update-core`,
          {
            record_id: attendanceId,
            updates,
            is_new: isNew,
            staff_id: editRecord.staff_id,
            attendance_date: editRecord.attendance_date,
            shift_code: editRecord.shift_code || null
          },
          {
            withCredentials: true,
            headers: {
              "X-Tenant": localStorage.getItem("tenant") || "",
            },
          }
        );
        savedRecordId = updateRes.data?.record_id || attendanceId;
      }

      if (remarkChanged) {
        const remarkRecordId = savedRecordId || editRecord.id;
        if (!remarkRecordId) {
          alert("Please enter clock details before saving a remark for a planned shift.");
          return;
        }
        await axios.put(
          `${API_URL}/admin/remark`,
          {
            record_id: remarkRecordId,
            work_type: editRecord.work_type,
            remark: editRemark
          },
          {
            withCredentials: true,
            headers: {
              "X-Tenant": localStorage.getItem("tenant") || "",
            },
          }
        );
      }

      setEditDialogOpen(false);
      fetchData(page, appliedFilters);

    } catch (err) {
      console.error("❌ Save failed:", err);
      alert("Save failed. Please try again.");
    }
  };

  const saveAbsenceResolution = async () => {
    try {
      if (!resolveRecord) return;
      const paid = Number(paidHours || 0);
      const unpaid = Number(unpaidHours || 0);
      const resolutionMode = getResolutionMode([resolveRecord]);
      const payload = resolveAttendancePayload({
        preset: allocationPreset,
        paidHours: paid,
        unpaidHours: unpaid,
        paidCategory,
        mode: resolutionMode,
      });
      const hasPaidAllocation = Number(payload.paid_minutes || 0) > 0 || ['FULL_PAID', 'HALF_PAID'].includes(payload.allocation_preset);
      if (resolutionMode !== 'incomplete' && allocationPreset === 'CUSTOM' && (paid < 0 || unpaid < 0 || paid + unpaid <= 0)) return alert("Enter paid or unpaid hours.");
      if (resolutionMode !== 'incomplete' && hasPaidAllocation && !paidCategory) return alert("Paid absence category is required.");
      if (!resolutionReason.trim()) return alert("Reason is required.");
      const date = toAttendanceDate(resolveRecord.attendance_date);
      await axios.post(`${API_URL}/admin/absence-resolutions`, {
        staff_id: resolveRecord.staff_id,
        attendance_date: date,
        ...payload,
        reason: resolutionReason.trim()
      }, { withCredentials: true, headers: { "X-Tenant": localStorage.getItem("tenant") || "" } });
      setResolveDialogOpen(false);
      fetchData(page, appliedFilters);
    } catch (err) {
      console.error("Absence resolution failed:", err);
      alert(err.response?.data?.message || "Could not save attendance resolution.");
    }
  };

  const openBulkResolveDialog = () => {
    if (!selectedResolvableRows.length) {
      alert("Select at least one absent or incomplete office attendance row.");
      return;
    }
    const allIncompleteLeave = selectedResolvableRows.every(
      row => row?.status === 'incomplete' && row?.day_type === 'LEAVE'
    );
    setBulkAllocationPreset(
      getResolutionMode(selectedResolvableRows) === 'incomplete'
        ? (allIncompleteLeave ? "CONFIRM_WORKED_REMAINDER" : "NO_TA_ONLY")
        : "FULL_PAID"
    );
    setBulkPaidHours(0);
    setBulkUnpaidHours(0);
    setBulkPaidCategory("AUTHORISED_PAID");
    setBulkResolutionReason("");
    setBulkResolveDialogOpen(true);
  };

  const saveBulkAbsenceResolution = async () => {
    try {
      const paid = Number(bulkPaidHours || 0);
      const unpaid = Number(bulkUnpaidHours || 0);
      const resolutionMode = getResolutionMode(selectedResolvableRows);
      const payload = resolveAttendancePayload({
        preset: bulkAllocationPreset,
        paidHours: paid,
        unpaidHours: unpaid,
        paidCategory: bulkPaidCategory,
        mode: resolutionMode,
      });
      const hasPaidAllocation = Number(payload.paid_minutes || 0) > 0 || ['FULL_PAID', 'HALF_PAID'].includes(payload.allocation_preset);
      if (!selectedResolvableRows.length) return alert("Select at least one absent or incomplete office attendance row.");
      if (resolutionMode !== 'incomplete' && bulkAllocationPreset === 'CUSTOM' && (paid < 0 || unpaid < 0 || paid + unpaid <= 0)) return alert("Enter paid or unpaid hours.");
      if (resolutionMode !== 'incomplete' && hasPaidAllocation && !bulkPaidCategory) return alert("Paid absence category is required.");
      if (!bulkResolutionReason.trim()) return alert("Reason is required.");

      const response = await axios.post(`${API_URL}/admin/absence-resolutions/bulk`, {
        records: selectedResolvableRows.map(row => ({
          staff_id: row.staff_id,
          attendance_date: toAttendanceDate(row.attendance_date)
        })),
        ...payload,
        reason: bulkResolutionReason.trim()
      }, { withCredentials: true, headers: { "X-Tenant": localStorage.getItem("tenant") || "" } });

      const result = response.data?.data || {};
      setBulkResolveDialogOpen(false);
      gridRef.current?.api?.deselectAll?.();
      setSelectedRows([]);
      fetchData(page, appliedFilters);
      alert(`Bulk resolution completed. Success: ${result.successCount || 0}, Failed: ${result.failureCount || 0}`);
    } catch (err) {
      console.error("Bulk attendance resolution failed:", err);
      alert(err.response?.data?.message || "Could not save bulk attendance resolutions.");
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <AppHeader onLogout={handleLogout} />

      <Box sx={{ p: { xs: 2, md: 2.5 }, maxWidth: "1600px", margin: "0 auto" }}>
        <HeadcountCards />
        <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.25, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a237e", mb: 0.25 }}>
                Attendance Records
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monitor and manage staff clock-in/out logs
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Tooltip title="Show or hide columns">
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ViewColumnIcon />}
                  onClick={() => {
                    refreshColumnOptions();
                    setColumnDialogOpen(true);
                  }}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Columns
                </Button>
              </Tooltip>

              <Tooltip title="Save current column order, width, pinning, filters, and visibility">
                <span>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<SaveIcon />}
                    disabled={gridPreferenceSaving}
                    onClick={handleSaveGridPreference}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Save View
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Restore the system default column view">
                <span>
                  <IconButton
                    onClick={handleResetGridPreference}
                    disabled={gridPreferenceSaving}
                    sx={{ bgcolor: '#f5f5f5' }}
                  >
                    <RestartAltIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon />}
                onClick={() => setFiltersOpen((prev) => !prev)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AutorenewIcon />}
                onClick={() => setProcessDialogOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Process Attendance
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FileDownloadIcon />}
                disabled={!appliedFilters.date_from || !appliedFilters.date_to}
                onClick={handleExportExcel}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Export Excel
              </Button>

              <Button
                variant="outlined"
                color="warning"
                startIcon={<RuleIcon />}
                disabled={!selectedResolvableRows.length}
                onClick={openBulkResolveDialog}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Bulk Resolve ({selectedResolvableRows.length})
              </Button>

              <Button
                variant="contained"
                startIcon={<FingerprintIcon />}
                onClick={() => navigate('/attendance/import')}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#1565c0',
                  '&:hover': { bgcolor: '#0d47a1' },
                }}
              >
                Import Fingerprint
              </Button>

              <IconButton onClick={handleRefresh} disabled={!appliedFilters.date_from || !appliedFilters.date_to} sx={{ bgcolor: '#f5f5f5' }}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Box>

          <Collapse in={filtersOpen}>
            <Paper variant="outlined" sx={{ p: 1.75, mb: 2, borderRadius: 2, bgcolor: '#fbfbfb' }}>
              <Stack direction="row" spacing={1.25} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                  sx={{ minWidth: 145 }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                  sx={{ minWidth: 145 }}
                />
                <TextField
                  label="Employee No."
                  size="small"
                  placeholder="Search..."
                  value={filters.staff_code}
                  onChange={(e) => setFilters((f) => ({ ...f, staff_code: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                  sx={{ minWidth: 145 }}
                />
                <TextField
                  label="Name"
                  size="small"
                  placeholder="Search name..."
                  value={filters.full_name}
                  onChange={(e) => setFilters((f) => ({ ...f, full_name: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                  sx={{ minWidth: 145 }}
                />
                <TextField
                  select
                  label="Branch"
                  size="small"
                  value={filters.branch_id}
                  onChange={(e) => setFilters((f) => ({ ...f, branch_id: e.target.value }))}
                  sx={{ minWidth: 145 }}
                >
                  <MenuItem value="">All Branches</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>{branch.branch_name}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Department"
                  size="small"
                  value={filters.org_node_id}
                  onChange={(e) => setFilters((f) => ({ ...f, org_node_id: e.target.value }))}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {orgNodes.map((node) => (
                    <MenuItem key={node.id} value={node.id} sx={{ pl: 2 + (node._depth || 0) * 2 }}>
                      {node._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                      {node.node_name}{node.level_type ? ` (${node.level_type})` : ''}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Project"
                  size="small"
                  value={filters.project_id}
                  onChange={(e) => setFilters((f) => ({ ...f, project_id: e.target.value }))}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">All Projects</MenuItem>
                  {projects.map((proj) => (
                    <MenuItem key={proj.id} value={proj.id}>
                      {proj.project_name} ({proj.project_code})
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Status"
                  size="small"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  sx={{ minWidth: 135 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="present">Present</MenuItem>
                  <MenuItem value="absent">Absent</MenuItem>
                  <MenuItem value="incomplete">Incomplete</MenuItem>
                  <MenuItem value="late">Late</MenuItem>
                  <MenuItem value="leave">Leave</MenuItem>
                  <MenuItem value="rest">Rest Day</MenuItem>
                </TextField>

                <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                  <Button variant="outlined" color="inherit" onClick={handleResetFilters} sx={{ borderRadius: 2, textTransform: 'none', height: 36 }}>
                    Reset
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Collapse>

          <div className="ag-theme-quartz" style={{ height: "min(620px, calc(100vh - 320px))", minHeight: 380, width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onSelectionChanged={(event) => setSelectedRows(event.api.getSelectedRows())}
              onGridReady={handleGridReady}
              onColumnMoved={refreshColumnOptions}
              onColumnVisible={refreshColumnOptions}
              onColumnPinned={refreshColumnOptions}
              onColumnResized={refreshColumnOptions}
              getRowStyle={(params) => {
                const note = params.data?.notes?.toLowerCase() || "";
                if (note.includes("manualeditbyhr")) {
                  return { backgroundColor: "#e3f2fd" }; // Light blue highlight for HR edits
                }
                if (note.includes("system-clockout")) {
                  return { backgroundColor: "#ffebee" }; // Light red highlight
                }
                if (
                  note.includes("manualentry") ||
                  note.includes("manualoverwrite") ||
                  note.includes("manualupdate")
                ) {
                  return { backgroundColor: "#eceff1" }; // Light grey highlight for approved manual attendance requests
                }
                return null;
              }}
              pagination={true}
              paginationPageSize={pageSize}
              suppressPaginationPanel={true}
              animateRows={true}
              rowHeight={40}
              headerHeight={42}
            />
          </div>

          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1.25,
            pt: 1,
            borderTop: "1px solid #e2e8f0"
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                Page Size:
              </Typography>
              <TextField
                select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                size="small"
                variant="outlined"
                sx={{
                  width: 80,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    height: 32,
                    fontSize: "0.875rem"
                  }
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
                <MenuItem value={1000}>1000</MenuItem>
              </TextField>
            </Stack>

            <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 600 }}>
              {total > 0 ? (page - 1) * pageSize + 1 : 0} to {Math.min(page * pageSize, total)} | Total Records: {total}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size="small"
                disabled={page <= 1}
                onClick={() => setPage(1)}
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid #cbd5e1",
                  bgcolor: "background.paper",
                  "&:disabled": { border: "1px solid #e2e8f0" }
                }}
              >
                <FirstPageIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid #cbd5e1",
                  bgcolor: "background.paper",
                  "&:disabled": { border: "1px solid #e2e8f0" }
                }}
              >
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>

              <Typography variant="body2" sx={{ color: "text.secondary", mx: 1 }}>
                Page <span style={{ fontWeight: 700, color: "#1e293b" }}>{page}</span> of <span style={{ fontWeight: 700, color: "#1e293b" }}>{totalPages || 1}</span>
              </Typography>

              <IconButton
                size="small"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid #cbd5e1",
                  bgcolor: "background.paper",
                  "&:disabled": { border: "1px solid #e2e8f0" }
                }}
              >
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid #cbd5e1",
                  bgcolor: "background.paper",
                  "&:disabled": { border: "1px solid #e2e8f0" }
                }}
              >
                <LastPageIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </Card>
      </Box>

      {rowData.map(row =>
        expandedRows[row.id] ? (
          <DetailRenderer key={`detail-${row.id}`} data={row} />
        ) : null
      )}

      <Dialog open={columnDialogOpen} onClose={() => setColumnDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Attendance List Columns</DialogTitle>
        <DialogContent dividers>
          {gridPreferenceLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading saved view...</Typography>
            </Box>
          ) : (
            <Stack spacing={0.5}>
              {columnOptions.map((column) => (
                <FormControlLabel
                  key={column.colId}
                  control={
                    <Checkbox
                      size="small"
                      checked={column.visible}
                      onChange={(event) => handleToggleColumn(column.colId, event.target.checked)}
                    />
                  }
                  label={column.label}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            disabled={gridPreferenceSaving}
            onClick={handleResetGridPreference}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={gridPreferenceSaving}
            onClick={handleSaveGridPreference}
          >
            Save View
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Attendance Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Attendance</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Clock In"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={editClockIn}
            onChange={(e) => setEditClockIn(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Clock Out"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={editClockOut}
            onChange={(e) => setEditClockOut(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="OT Hours"
            type="number"
            fullWidth
            margin="normal"
            value={editOtHours}
            onChange={(e) => setEditOtHours(e.target.value)}
            inputProps={{ step: "0.25", min: "0" }}
          />
          <TextField
            label="Remark"
            type="text"
            fullWidth
            margin="normal"
            value={editRemark}
            onChange={(e) => setEditRemark(e.target.value)}
            placeholder="e.g., ဆေးခွင့်, နေမကောင်းပါသဖြင့်"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAttendanceChanges}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{singleResolutionMode === 'incomplete' ? 'Resolve Incomplete Attendance' : 'Resolve Attendance'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {resolveRecord?.full_name} — {toAttendanceDate(resolveRecord?.attendance_date)}. The server validates these units against the assigned shift.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField select fullWidth label={singleResolutionMode === 'incomplete' ? 'Incomplete Resolution' : 'Allocation'} value={allocationPreset} onChange={e => setAllocationPreset(e.target.value)}>
              {singleResolutionOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField></Grid>
            {singleResolutionMode !== 'incomplete' && (
              <>
                <Grid item xs={6}><TextField fullWidth disabled={allocationPreset !== 'CUSTOM'} type="number" label="Paid Hours" value={paidHours} onChange={e => setPaidHours(e.target.value)} inputProps={{ min: 0, step: 0.5 }} /></Grid>
                <Grid item xs={6}><TextField fullWidth disabled={allocationPreset !== 'CUSTOM'} type="number" label="Unpaid Hours" value={unpaidHours} onChange={e => setUnpaidHours(e.target.value)} inputProps={{ min: 0, step: 0.5 }} /></Grid>
                <Grid item xs={12}><TextField select fullWidth label="Paid Absence Category" value={paidCategory} onChange={e => setPaidCategory(e.target.value)} disabled={Number(paidHours || 0) <= 0 && !['FULL_PAID', 'HALF_PAID'].includes(allocationPreset)}>
                  <MenuItem value="AUTHORISED_PAID">Authorised Paid Absence</MenuItem>
                  <MenuItem value="OFFICIAL_DUTY">Official Duty</MenuItem>
                  <MenuItem value="TRAINING">Training</MenuItem>
                  <MenuItem value="OTHER_PAID">Other Paid Absence</MenuItem>
                </TextField></Grid>
              </>
            )}
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Reason" value={resolutionReason} onChange={e => setResolutionReason(e.target.value)} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAbsenceResolution}>Save Resolution</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkResolveDialogOpen} onClose={() => setBulkResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{bulkResolutionMode === 'incomplete' ? 'Bulk Resolve Incomplete Attendance' : 'Bulk Resolve Attendance'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Apply one resolution to {selectedResolvableRows.length} selected absent or incomplete office rows. The server validates every row against its assigned shift.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth label={bulkResolutionMode === 'incomplete' ? 'Incomplete Resolution' : 'Allocation'} value={bulkAllocationPreset} onChange={e => setBulkAllocationPreset(e.target.value)}>
                {bulkResolutionOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {bulkResolutionMode !== 'incomplete' && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth disabled={bulkAllocationPreset !== 'CUSTOM'} type="number" label="Paid Hours" value={bulkPaidHours} onChange={e => setBulkPaidHours(e.target.value)} inputProps={{ min: 0, step: 0.5 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth disabled={bulkAllocationPreset !== 'CUSTOM'} type="number" label="Unpaid Hours" value={bulkUnpaidHours} onChange={e => setBulkUnpaidHours(e.target.value)} inputProps={{ min: 0, step: 0.5 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField select fullWidth label="Paid Absence Category" value={bulkPaidCategory} onChange={e => setBulkPaidCategory(e.target.value)} disabled={Number(bulkPaidHours || 0) <= 0 && !['FULL_PAID', 'HALF_PAID'].includes(bulkAllocationPreset)}>
                    <MenuItem value="AUTHORISED_PAID">Authorised Paid Absence</MenuItem>
                    <MenuItem value="OFFICIAL_DUTY">Official Duty</MenuItem>
                    <MenuItem value="TRAINING">Training</MenuItem>
                    <MenuItem value="OTHER_PAID">Other Paid Absence</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Reason" value={bulkResolutionReason} onChange={e => setBulkResolutionReason(e.target.value)} placeholder="e.g. Flexible attendance policy for this staff group" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkResolveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveBulkAbsenceResolution}>Apply to Selected</Button>
        </DialogActions>
      </Dialog>

      {/* Photo Preview Dialog */}
      <Dialog
        open={photoDialogOpen}
        onClose={handleClosePhotoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{photoTitle || "Photo"}</DialogTitle>
        <DialogContent dividers>
          {photoLoading ? (
            <Typography variant="body2">Loading photo...</Typography>
          ) : photoError ? (
            <Typography variant="body2" color="error">
              {photoError}
            </Typography>
          ) : photoUrl ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <img
                src={photoUrl}
                alt={photoTitle || "Photo"}
                style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }}
              />
            </Box>
          ) : (
            <Typography variant="body2">No photo available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhotoDialog}>Close</Button>
          {photoUrl && (
            <Button
              variant="outlined"
              onClick={() => window.open(photoUrl, "_blank", "noopener,noreferrer")}
            >
              Open in new tab
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Process Attendance Dialog */}
      <Dialog open={processDialogOpen} onClose={() => !processLoading && setProcessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e' }}>Process Attendance</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Run the Time Evaluation engine for a specific date range. This will reconcile shifts, check for leaves, and highlight absent staff.
          </Typography>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={processStartDate}
              onChange={(e) => setProcessStartDate(e.target.value)}
              disabled={processLoading}
            />
            <TextField
              fullWidth
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={processEndDate}
              onChange={(e) => setProcessEndDate(e.target.value)}
              disabled={processLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setProcessDialogOpen(false)} color="inherit" disabled={processLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleProcessAttendance}
            variant="contained"
            color="secondary"
            disabled={processLoading}
            startIcon={processLoading ? <CircularProgress size={20} color="inherit" /> : <AutorenewIcon />}
          >
            {processLoading ? "Processing..." : "Run Evaluation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Dialog */}
      <Dialog
        open={mapDialogOpen}
        onDismiss={() => setMapDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{mapTitle}</Typography>
          <IconButton onClick={() => setMapDialogOpen(false)} size="small">
            <ExpandLessIcon sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 450 }}>
          {mapDialogOpen && (
            <MapContainer center={[mapLat, mapLng]} zoom={15} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[mapLat, mapLng]}>
                <Popup>{mapTitle}</Popup>
              </Marker>
              <MapUpdater center={[mapLat, mapLng]} />
            </MapContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => setMapDialogOpen(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper to update map center when dialog re-opens
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center);
  }, [center, map]);
  return null;
}

export default AttendanceList;
